import "./QuestionVoteField.scss";

import {
  AddIcon,
  Alert,
  Button,
  ButtonGroup,
  Checkbox,
  Datepicker,
  Flex,
  Input,
  RadioGroup,
  TextArea,
} from "@fluentui/react-northstar";
import FormState, { FieldDescriptor } from "@shopify/react-form-state";
import { SurveyLimits } from "@teams/cards";
import {
  LabeledQuestionChoice,
  NominalQuestion,
  OpenEndedQuestion,
  OrdinalQuestion,
  Question,
  QuestionChoice,
  QuestionType,
} from "@teams/types";
import moment from "moment";
import React, { useEffect, useState } from "react";

import { DropdownField, TextInputField } from "../../../../_shared/components/fields";
import { PollyIcon } from "../../../../_shared/components/PollyIcon";
import { isError } from "../../../../_shared/services/ChannelApi";

export const QuestionVoteField: React.FC<{
  question: Question<QuestionType>;
  onChoiceAddedSubmit?: (questionId: string, choice: string) => Promise<Error | void>;
  field?: FieldDescriptor<string>;
}> = ({ question, field, onChoiceAddedSubmit }) => {
  switch (question?.questionType) {
    case "open-ended":
      return <OpenEndedVote question={question as OpenEndedQuestion} field={field} />;
    case "ordinal":
      return (
        <ChoiceField
          question={question as OrdinalQuestion}
          field={field}
          onChoiceAddedSubmit={onChoiceAddedSubmit}
        />
      );
    case "nominal":
      return (
        <ChoiceField
          question={question as NominalQuestion}
          field={field}
          onChoiceAddedSubmit={onChoiceAddedSubmit}
        />
      );
    default:
      return null;
  }
};

const OpenEndedVote: React.FC<{
  question: OpenEndedQuestion;
  field?: FieldDescriptor<string>;
}> = ({ question, field }) => {
  if (question.contentType === "text/plain") {
    return (
      <Flex column>
        <TextArea
          fluid
          placeholder="Enter your response"
          readOnly={!field}
          maxLength={SurveyLimits.Questions.OpenEnded.Value.MaxLength}
          variables={{ height: "100px" }}
          onChange={(_, b) => {
            field?.onChange(b?.value as string);
          }}
          defaultValue={field?.value}
        />
        {field?.error && <Alert danger attached="bottom" content={field.error} />}
      </Flex>
    );
  }

  if (!field) {
    return <Input readOnly fluid />;
  }

  if (question.contentType === "text/time") {
    return (
      <Flex>
        <Flex.Item shrink>
          <div className="open-ended-vote-field">
            <DropdownField<string> field={field} items={generateTimeSeries(30)} />
          </div>
        </Flex.Item>
      </Flex>
    );
  }

  if (question.contentType === "text/date") {
    const selectedDate = moment(field.value).isValid() ? moment(field.value).toDate() : undefined;
    return (
      <Datepicker
        allowManualInput={false}
        selectedDate={selectedDate}
        onDateChange={(_, v) => {
          if (v) {
            field.onChange(moment(v.value).format("YYYY-MM-DD"));
          }
        }}
        input={{
          value: moment(selectedDate).format("LL"),
        }}
      />
    );
  }

  return null;
};

const ChoiceField: React.FC<{
  question: ChoiceQuestion;
  onChoiceAddedSubmit?: (questionId: string, choice: string) => Promise<Error | void>;
  field?: FieldDescriptor<string>;
}> = ({ question, field, onChoiceAddedSubmit }) => {
  if (question.allowMultipleChoices) {
    return (
      <MultiChoiceField
        question={question}
        field={field}
        onChoiceAddedSubmit={onChoiceAddedSubmit}
      />
    );
  }

  return (
    <SingleChoiceField
      question={question}
      field={field}
      onChoiceAddedSubmit={onChoiceAddedSubmit}
    />
  );
};

const SingleChoiceField: React.FC<{
  question: ChoiceQuestion;
  onChoiceAddedSubmit?: (questionId: string, choice: string) => Promise<Error | void>;
  field?: FieldDescriptor<string>;
}> = ({ question, field, onChoiceAddedSubmit }) => {
  const [isAddingChoice, setIsAddingChoice] = useState<boolean>();

  return (
    <Flex column gap="gap.small" vAlign="start" className="single-choice-field">
      <RadioGroup
        vertical
        checkedValue={field?.value}
        items={question.choices.map(choice => ({
          name: choice.id,
          key: choice.id,
          label: choice.label || choice.value,
          value: choice.id,
        }))}
        onCheckedValueChange={(_e, props) => {
          if (!field || !props) {
            return;
          }

          field.onChange(props.value as string);
        }}
        className="single-choice-field__radio-group"
      />

      {question.allowUserAddOptions &&
        onChoiceAddedSubmit &&
        question.choices.length < SurveyLimits.Questions.Choices.Max && (
          <div>
            {!isAddingChoice && (
              <Button
                size="small"
                content="Add choice"
                icon={<AddIcon />}
                iconPosition="before"
                onClick={() => {
                  field?.onChange("");
                  setIsAddingChoice(true);
                }}
              />
            )}

            {isAddingChoice && (
              <AddChoiceForm
                onClose={() => setIsAddingChoice(false)}
                question={question}
                onSubmit={onChoiceAddedSubmit}
              />
            )}
          </div>
        )}
    </Flex>
  );
};

const MultiChoiceField: React.FC<{
  question: ChoiceQuestion;
  field?: FieldDescriptor<string>;
  onChoiceAddedSubmit?: (questionId: string, choice: string) => Promise<Error | void>;
}> = ({ question, field, onChoiceAddedSubmit }) => {
  const [isAddingChoice, setIsAddingChoice] = useState<boolean>();
  const [selectedValues, setSelectedValues] = useState<string[]>([]);

  useEffect(() => {
    if (!field) {
      return;
    }

    field.onChange(selectedValues.join(","));
  }, [selectedValues, field]);

  return (
    <Flex column gap="gap.medium">
      <Flex column gap="gap.smaller">
        {question.choices.map(choice => (
          <div key={choice.id}>
            <Checkbox
              checked={selectedValues.includes(choice.id)}
              label={choice.label || choice.value.toString()}
              onChange={(_, b) => {
                if (!field) {
                  return;
                }

                if (!question.allowMultipleChoices) {
                  setSelectedValues([choice.id]);
                  return;
                }

                if (b?.checked && !selectedValues.includes(choice.id)) {
                  setSelectedValues([...selectedValues, choice.id]);
                }

                if (!b?.checked) {
                  setSelectedValues(selectedValues.filter(c => c !== choice.id));
                }
              }}
            />
            {field?.error && <Alert danger attached="bottom" content={field.error} />}
          </div>
        ))}
      </Flex>

      {question.allowUserAddOptions &&
        onChoiceAddedSubmit &&
        question.choices.length < SurveyLimits.Questions.Choices.Max && (
          <div>
            {!isAddingChoice && (
              <Button
                size="small"
                content="Add choice"
                icon={<AddIcon size="small" />}
                iconPosition="before"
                onClick={() => {
                  field?.onChange("");
                  setIsAddingChoice(true);
                }}
              />
            )}

            {isAddingChoice && (
              <AddChoiceForm
                onClose={() => setIsAddingChoice(false)}
                question={question}
                onSubmit={onChoiceAddedSubmit}
              />
            )}
          </div>
        )}
    </Flex>
  );
};

export type ChoiceQuestion = {
  id: string;
  allowMultipleChoices?: boolean;
  allowUserAddOptions?: boolean;
  choices: ChoiceQuestionChoices;
};

export type ChoiceQuestionChoices = Array<
  QuestionChoice<string | number> & Partial<LabeledQuestionChoice>
>;

function generateTimeSeries(minutesStep: number): { label: string; value: string }[] {
  const dt = new Date(1970, 0, 1);
  const rc: { label: string; value: string }[] = [];
  while (dt.getDate() === 1) {
    const value = dt.toLocaleTimeString([], { hour12: true, hour: "numeric", minute: "2-digit" });
    rc.push({
      label: value,
      value,
    });
    dt.setMinutes(dt.getMinutes() + minutesStep);
  }
  return rc;
}

type AddChoiceFormProps = {
  onClose: () => void;
  onSubmit: (questionId: string, choice: string) => Promise<void | Error>;
  question: ChoiceQuestion;
};

const AddChoiceForm: React.FC<AddChoiceFormProps> = ({ question, onClose, onSubmit }) => {
  return (
    <FormState
      initialValues={{ value: "" }}
      onSubmit={async ({ fields }) => {
        if (!fields.value.value) {
          return [new Error("Value is required!")];
        }

        if (
          question.choices.some(
            e => (e.value as string).toLocaleLowerCase() === fields.value.value.toLocaleLowerCase()
          )
        ) {
          return [new Error("Choice already exists!")];
        }

        const result = await onSubmit(question.id, fields.value.value);
        if (isError(result)) {
          return [result];
        } else {
          onClose();
        }
      }}
    >
      {({ fields, submitting, submit, errors }) => {
        return (
          <Flex column gap="gap.smaller">
            <Flex gap="gap.smaller" vAlign="center">
              <TextInputField
                placeholder="Enter new choice"
                autoFocus
                fluid
                field={fields.value}
                readOnly={submitting}
                maxLength={SurveyLimits.Questions.Choices.Value.MaxLength}
                onKeyDown={e => {
                  if (e.key === "Enter") {
                    submit();
                    e.preventDefault();
                  }
                }}
              />
              <ButtonGroup
                buttons={[
                  {
                    key: "add",
                    content: "Submit",
                    disabled: !fields.value.value,
                    secondary: true,
                    size: "medium",
                    icon: <PollyIcon icon="blue-check-icon" />,
                    loading: submitting,
                    onClick: () => submit(),
                  },
                  {
                    key: "cancel",
                    icon: <PollyIcon icon="trash-icon" />,
                    iconOnly: true,
                    text: true,
                    disabled: submitting,
                    onClick: () => onClose(),
                  },
                ]}
              />
            </Flex>
            {errors?.length > 0 && <Alert danger content={errors.join(",")} />}
          </Flex>
        );
      }}
    </FormState>
  );
};
